/**
 * Derive a full playback bundle (chapters / key moments / timeline /
 * clips / stats) from an UPLOADED recording's transcript cues — the
 * live counterpart to the authored `playback-content.ts` fixture, so
 * the PlaybackModal renders real uploads with the same four-tab
 * experience instead of a bare player (UAT round 5).
 *
 * Pure + deterministic (no React, no stores, no randomness): the same
 * transcript always derives the same bundle, so this is trivially
 * unit-tested and safe to recompute at render time. Everything is
 * heuristic-from-cues:
 *   - chapters   — split at the largest silence gaps (fallback: equal
 *                  time windows), titled by their opening line;
 *   - moments    — keyword-classified cues (decision / action / risk /
 *                  question / AI), longest-cue highlights as filler;
 *   - timeline   — chapters on the flow track, per-speaker blocks on
 *                  the speaking track, moments on the events track;
 *   - speaking   — per-speaker share of voiced time;
 *   - clips      — the top moments, cut to the gap before the next one;
 *   - stats      — duration / speakers / exchanges / flagged moments.
 *
 * Only SYNCED transcripts (every cue carries a start time) can derive
 * the time-based surfaces; `derivePlayback` returns null for unsynced
 * bodies and the modal falls back to the plain player.
 */

import type { Transcript } from "@/stores/useTranscripts";
import {
  formatCueTime,
  type Cue,
  type ParsedTranscript,
} from "@/features/ws4-transcript/transcript-cues";
import type {
  Clip,
  ClipAccent,
  KeyMoment,
  KeyMomentAccent,
  PlaybackChapter,
  PlaybackStat,
  SpeakerRow,
  TimelineSegment,
  WorkshopPlayback,
} from "./playback-content";
import type {
  TranscriptBlock,
  TranscriptTag,
} from "@/features/ws4-transcript/transcript-content";

/* ------------------------------ Tuning ------------------------------ */

/** Chapter count bounds — enough structure to navigate, not a TOC. */
const MIN_CHAPTERS = 2;
const MAX_CHAPTERS = 6;
/** A silence gap must be at least this long to count as a chapter break. */
const MIN_BREAK_GAP_MS = 20_000;
/** Cap on a single cue's assumed voiced duration when no end/next exists. */
const MAX_CUE_MS = 30_000;
/** How many key moments (and clips) to surface at most. */
const MAX_MOMENTS = 8;
const MAX_CLIPS = 6;

const CHAPTER_ACCENTS = [
  "true-blue",
  "sky",
  "gold",
  "green",
  "melon",
  "red",
] as const;

const SPEAKER_ACCENTS = [
  "true-blue",
  "sky",
  "green",
  "gold",
  "melon",
  "pink",
] as const;

/* --------------------------- Cue utilities -------------------------- */

/** Voiced duration of cue i — explicit end, else gap to the next cue,
 *  else the cap. Always clamped to [0, MAX_CUE_MS]. */
function cueDurationMs(cues: Cue[], i: number): number {
  const cue = cues[i];
  if (!cue) return 0;
  const next = cues[i + 1];
  const raw =
    cue.endMs !== undefined && Number.isFinite(cue.endMs)
      ? cue.endMs - cue.startMs
      : next
        ? next.startMs - cue.startMs
        : MAX_CUE_MS;
  return Math.max(0, Math.min(raw, MAX_CUE_MS));
}

/** Total session length: last voice offset plus its voiced duration. */
function totalDurationMs(cues: Cue[]): number {
  if (cues.length === 0) return 0;
  const last = cues[cues.length - 1]!;
  return last.startMs + cueDurationMs(cues, cues.length - 1);
}

/** First ~n words of a cue, for titles. */
function snippet(text: string, words: number): string {
  const parts = text.trim().split(/\s+/);
  const cut = parts.slice(0, words).join(" ");
  return parts.length > words ? `${cut}…` : cut;
}

/** `h:mm` (over an hour) or `m:ss` compact duration label. */
function shortDuration(ms: number): string {
  const totalMin = Math.round(ms / 60_000);
  if (totalMin >= 60) return `${Math.floor(totalMin / 60)}h ${totalMin % 60}m`;
  return `${Math.max(totalMin, 1)} min`;
}

/* ---------------------------- Classification ------------------------ */

type MomentClass = {
  tagLabel: string;
  accent: KeyMomentAccent;
  clipAccent: ClipAccent;
  icon: string;
  /** Higher wins when a cue matches several classes. */
  weight: number;
};

const MOMENT_CLASSES: ReadonlyArray<{ re: RegExp; cls: MomentClass }> = [
  {
    re: /\b(decision|decide[ds]?|agree[ds]?|approved?|sign[- ]?off)\b/i,
    cls: {
      tagLabel: "DECISION",
      accent: "green",
      clipAccent: "green",
      icon: "✅",
      weight: 5,
    },
  },
  {
    re: /\b(action|next step|follow[- ]?up|to[- ]?do|will (send|set up|arrange|draft|book))\b/i,
    cls: {
      tagLabel: "ACTION ITEM",
      accent: "true-blue",
      clipAccent: "true-blue",
      icon: "📌",
      weight: 4,
    },
  },
  {
    re: /\b(risk|concern|worr(y|ied)|issue|blocker|problem|escalat)\w*\b/i,
    cls: {
      tagLabel: "RISK FLAG",
      accent: "red",
      clipAccent: "red",
      icon: "⚠️",
      weight: 4,
    },
  },
  {
    re: /\b(agent|xaes|ai\b|model|automation|copilot)\b/i,
    cls: {
      tagLabel: "AI NOTE",
      accent: "gold",
      clipAccent: "gold",
      icon: "🤖",
      weight: 2,
    },
  },
  {
    re: /\?\s*$/,
    cls: {
      tagLabel: "QUESTION",
      accent: "sky",
      clipAccent: "melon",
      icon: "❓",
      weight: 1,
    },
  },
];

/** Classify one cue; null when nothing interesting matches. */
export function classifyCue(text: string): MomentClass | null {
  let best: MomentClass | null = null;
  for (const { re, cls } of MOMENT_CLASSES) {
    if (re.test(text) && (best === null || cls.weight > best.weight)) {
      best = cls;
    }
  }
  return best;
}

/* ------------------------------ Chapters ---------------------------- */

/** Indices where a new chapter starts (always includes 0). */
function chapterStartIndices(cues: Cue[], durationMs: number): number[] {
  const target = Math.min(
    Math.max(Math.round(durationMs / 600_000), MIN_CHAPTERS),
    MAX_CHAPTERS,
  );
  if (cues.length <= target) return cues.map((_, i) => i).slice(0, target);

  // Prefer natural breaks: the largest inter-cue gaps above the floor.
  const gaps: Array<{ index: number; gap: number }> = [];
  for (let i = 1; i < cues.length; i++) {
    const gap = cues[i]!.startMs - cues[i - 1]!.startMs;
    if (gap >= MIN_BREAK_GAP_MS) gaps.push({ index: i, gap });
  }
  gaps.sort((a, b) => b.gap - a.gap);
  const breaks = gaps
    .slice(0, target - 1)
    .map((g) => g.index)
    .sort((a, b) => a - b);
  if (breaks.length === target - 1) return [0, ...breaks];

  // Not enough silence — fall back to equal TIME windows, snapped to
  // the first cue at/after each boundary.
  const starts = [0];
  for (let k = 1; k < target; k++) {
    const boundary = (durationMs * k) / target;
    const idx = cues.findIndex((c) => c.startMs >= boundary);
    const prev = starts[starts.length - 1]!;
    if (idx > prev) starts.push(idx);
  }
  return starts;
}

type DerivedChapter = PlaybackChapter & {
  startMs: number;
  endMs: number;
  cueCount: number;
};

function deriveChapters(cues: Cue[], durationMs: number): DerivedChapter[] {
  const starts = chapterStartIndices(cues, durationMs);
  return starts.map((startIdx, n) => {
    const endIdx = n + 1 < starts.length ? starts[n + 1]! : cues.length;
    const first = cues[startIdx]!;
    const startMs = first.startMs;
    const endMs =
      endIdx < cues.length ? cues[endIdx]!.startMs : durationMs;
    const slice = cues.slice(startIdx, endIdx);
    const speakers = new Set(
      slice.map((c) => c.speaker).filter((s): s is string => !!s),
    );
    return {
      id: `dch-${n}`,
      title: `${n + 1}. ${snippet(first.text, 7)}`,
      timestamp: formatCueTime(startMs),
      duration: shortDuration(endMs - startMs),
      description:
        `${slice.length} exchange${slice.length === 1 ? "" : "s"}` +
        (speakers.size > 0
          ? ` · ${[...speakers].slice(0, 3).join(", ")}`
          : ""),
      accent: CHAPTER_ACCENTS[n % CHAPTER_ACCENTS.length]!,
      jumpMinutes: Math.floor(startMs / 60_000),
      startMs,
      endMs,
      cueCount: slice.length,
    };
  });
}

/* ----------------------------- Key moments -------------------------- */

type DerivedMoment = KeyMoment & { startMs: number; clipAccent: ClipAccent };

function deriveMoments(cues: Cue[]): DerivedMoment[] {
  const classified: Array<{ cue: Cue; index: number; cls: MomentClass }> = [];
  for (let i = 0; i < cues.length; i++) {
    const cls = classifyCue(cues[i]!.text);
    if (cls) classified.push({ cue: cues[i]!, index: i, cls });
  }
  // Highest-signal first, then earliest; cap, then re-sort by time.
  classified.sort(
    (a, b) => b.cls.weight - a.cls.weight || a.cue.startMs - b.cue.startMs,
  );
  let picked = classified.slice(0, MAX_MOMENTS);

  // Thin transcripts: pad with the longest cues so the tab isn't empty.
  if (picked.length < Math.min(3, cues.length)) {
    const chosen = new Set(picked.map((p) => p.cue.id));
    const fillers = cues
      .map((cue, index) => ({ cue, index }))
      .filter(({ cue }) => !chosen.has(cue.id))
      .sort((a, b) => b.cue.text.length - a.cue.text.length)
      .slice(0, Math.min(3, cues.length) - picked.length)
      .map(({ cue, index }) => ({
        cue,
        index,
        cls: {
          tagLabel: "HIGHLIGHT",
          accent: "sky",
          clipAccent: "melon",
          icon: "⭐",
          weight: 0,
        } as MomentClass,
      }));
    picked = [...picked, ...fillers];
  }

  picked.sort((a, b) => a.cue.startMs - b.cue.startMs);
  return picked.map(({ cue, cls }, n) => ({
    id: `dkm-${n}`,
    timestamp: formatCueTime(cue.startMs),
    icon: cls.icon,
    title: snippet(cue.text, 8),
    description: cue.speaker ? `${cue.speaker}: ${snippet(cue.text, 24)}` : snippet(cue.text, 24),
    tagLabel: cls.tagLabel,
    accent: cls.accent,
    jumpMinutes: Math.floor(cue.startMs / 60_000),
    startMs: cue.startMs,
    clipAccent: cls.clipAccent,
  }));
}

/* ------------------------------ Timeline ---------------------------- */

const CHAPTER_TO_TIMELINE = {
  "true-blue": "true-blue",
  sky: "sky",
  gold: "gold",
  green: "green",
  melon: "melon",
  red: "red-pink",
} as const;

function deriveTimeline(
  cues: Cue[],
  chapters: DerivedChapter[],
  moments: DerivedMoment[],
  durationMs: number,
): TimelineSegment[] {
  const pct = (ms: number) =>
    Math.max(0, Math.min(100, (ms / durationMs) * 100));
  const segments: TimelineSegment[] = [];

  // Flow track — one segment per chapter.
  chapters.forEach((ch, i) => {
    const left = pct(ch.startMs);
    const width = Math.max(pct(ch.endMs) - left, 1.5);
    segments.push({
      id: `tlf-${i}`,
      track: "flow",
      label: width > 8 ? snippet(ch.title.replace(/^\d+\. /, ""), 3) : "",
      left,
      width,
      accent: CHAPTER_TO_TIMELINE[ch.accent],
    });
  });

  // Speaking track — consecutive same-speaker runs, alternating fills.
  let runStart = 0;
  let fillFlip = false;
  for (let i = 1; i <= cues.length; i++) {
    const prev = cues[i - 1]!;
    const speakerChanged = i === cues.length || cues[i]!.speaker !== prev.speaker;
    if (!speakerChanged) continue;
    const startMs = cues[runStart]!.startMs;
    const endMs = prev.startMs + cueDurationMs(cues, i - 1);
    const left = pct(startMs);
    const width = Math.max(pct(endMs) - left, 0.8);
    const speaker = cues[runStart]!.speaker ?? "";
    segments.push({
      id: `tls-${runStart}`,
      track: "speaking",
      label: width > 7 ? speaker : "",
      left,
      width,
      accent: fillFlip ? "true-blue-fill" : "sky-fill",
    });
    fillFlip = !fillFlip;
    runStart = i;
  }

  // Events track — one thin marker per key moment.
  moments.forEach((m, i) => {
    segments.push({
      id: `tle-${i}`,
      track: "events",
      label: "",
      left: pct(m.startMs),
      width: 1.2,
      accent: CHAPTER_TO_TIMELINE[m.accent === "sky" ? "sky" : m.accent] ?? "sky",
    });
  });

  return segments;
}

/* ---------------------------- Speaking time ------------------------- */

function deriveSpeakingTime(cues: Cue[]): SpeakerRow[] {
  const bySpeaker = new Map<string, { ms: number; turns: number }>();
  let voicedTotal = 0;
  cues.forEach((cue, i) => {
    const ms = cueDurationMs(cues, i);
    voicedTotal += ms;
    const key = cue.speaker ?? "Unattributed";
    const entry = bySpeaker.get(key) ?? { ms: 0, turns: 0 };
    entry.ms += ms;
    entry.turns += 1;
    bySpeaker.set(key, entry);
  });
  if (voicedTotal === 0) return [];
  return [...bySpeaker.entries()]
    .sort((a, b) => b[1].ms - a[1].ms)
    .map(([speaker, { ms, turns }], i) => ({
      speaker,
      role: speaker === "Unattributed" ? "—" : "Participant",
      estTime: shortDuration(ms),
      percent: `${((ms / voicedTotal) * 100).toFixed(1)}%`,
      contribution: `${turns} contribution${turns === 1 ? "" : "s"}`,
      accent:
        speaker === "Unattributed"
          ? "muted"
          : SPEAKER_ACCENTS[i % SPEAKER_ACCENTS.length]!,
    }));
}

/* -------------------------------- Clips ----------------------------- */

/** Deterministic 20–80 pseudo-position from a string (no randomness —
 *  the same clip always paints the same decorative thumb). */
function hashPct(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return 20 + (Math.abs(h) % 61);
}

function deriveClips(
  moments: DerivedMoment[],
  durationMs: number,
): Clip[] {
  return moments.slice(0, MAX_CLIPS).map((m, i) => {
    const next = moments[i + 1];
    const clipMs = Math.min(
      Math.max((next ? next.startMs : durationMs) - m.startMs, 15_000),
      120_000,
    );
    return {
      id: `dcl-${i}`,
      title: m.title,
      timestamp: m.timestamp,
      speaker: m.description.includes(":")
        ? m.description.slice(0, m.description.indexOf(":"))
        : "—",
      tagLabel: m.tagLabel,
      tagAccent: m.clipAccent,
      duration: formatCueTime(clipMs),
      jumpMinutes: m.jumpMinutes,
      thumbGradientX: hashPct(`${m.id}-x`),
      thumbGradientY: hashPct(`${m.id}-y`),
    };
  });
}

/* ------------------------------- Bundle ----------------------------- */

export type DerivedPlayback = WorkshopPlayback & {
  /** Chapter time bounds — lets the modal seek the real recording. */
  chapterBounds: ReadonlyArray<{ id: string; startMs: number }>;
};

/**
 * Derive the full playback bundle for one uploaded recording. Returns
 * null when the transcript isn't synced (no per-cue timestamps) — the
 * caller falls back to the plain player.
 */
export function derivePlayback(
  transcript: Transcript,
  parsed: ParsedTranscript,
  sessionLabel: string,
): DerivedPlayback | null {
  if (!parsed.synced || parsed.cues.length === 0) return null;
  const cues = [...parsed.cues].sort((a, b) => a.startMs - b.startMs);
  const durationMs = Math.max(totalDurationMs(cues), 60_000);

  const chapters = deriveChapters(cues, durationMs);
  const moments = deriveMoments(cues);
  const timelineSegments = deriveTimeline(cues, chapters, moments, durationMs);
  const speakingTime = deriveSpeakingTime(cues);
  const clips = deriveClips(moments, durationMs);

  const speakers = new Set(
    cues.map((c) => c.speaker).filter((s): s is string => !!s),
  );
  const durationLabel = formatCueTime(durationMs);
  const addedDate = new Date(transcript.addedAt).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  const stats: PlaybackStat[] = [
    {
      label: "Session Duration",
      value: durationLabel,
      sub: "from the recording",
      accent: "sky",
    },
    {
      label: "Speakers",
      value: String(Math.max(speakers.size, 1)),
      sub: "voices in the transcript",
      accent: "green",
    },
    {
      label: "Exchanges",
      value: String(cues.length),
      sub: "transcript cues",
      accent: "true-blue",
    },
    {
      label: "Key Moments",
      value: String(moments.length),
      sub: "auto-flagged",
      accent: "gold",
    },
  ];

  const ticks: string[] = [];
  const TICKS = 8;
  for (let t = 0; t < TICKS; t++) {
    ticks.push(formatCueTime(Math.round((durationMs * t) / (TICKS - 1))));
  }

  return {
    sessionTitle: transcript.title,
    sessionSubtitle: `${sessionLabel} · ${addedDate}`,
    durationBadge: durationLabel,
    scrubberLabel: `0:00 / ${durationLabel}`,
    footerSummary:
      `${shortDuration(durationMs)} · ${chapters.length} CHAPTERS · ` +
      `${moments.length} KEY MOMENTS · ${clips.length} CLIPS`,
    timelineTitle: `Session Timeline — ${shortDuration(durationMs)} · ${addedDate}`,
    timelineTicks: ticks,
    scrubberChapterMarkers: chapters.map((ch) => ({
      left: Math.max(0, Math.min(100, (ch.startMs / durationMs) * 100)),
      title: ch.title,
    })),
    stats,
    chapters,
    keyMoments: moments,
    timelineSegments,
    speakingTime,
    clips,
    chapterBounds: chapters.map((ch) => ({ id: ch.id, startMs: ch.startMs })),
  };
}

/* ----------------------- Transcript breakdown ----------------------- */

export type DerivedTranscriptSection = {
  key: string;
  label: string;
  blocks: TranscriptBlock[];
};

/** Map a moment class onto the transcript line-tag vocabulary. */
function lineTag(
  cls: MomentClass | null,
): { tag: TranscriptTag; tagText: string } | null {
  if (!cls) return null;
  switch (cls.tagLabel) {
    case "ACTION ITEM":
      return { tag: "action", tagText: "ACTION" };
    case "RISK FLAG":
      return { tag: "flag", tagText: "RISK" };
    case "AI NOTE":
      return { tag: "ai", tagText: "AI" };
    case "DECISION":
      return { tag: "note", tagText: "DECISION" };
    default:
      return null; // questions/highlights stay untagged — too noisy
  }
}

function cueToLine(cue: Cue, i: number): TranscriptBlock {
  const tagged = lineTag(classifyCue(cue.text));
  return {
    kind: "line",
    id: `dtl-${i}`,
    time: Number.isFinite(cue.startMs) ? formatCueTime(cue.startMs) : "",
    speaker: cue.speaker ?? "—",
    text: cue.text,
    ...(tagged ?? {}),
  };
}

/**
 * Break an uploaded transcript into the authored-style tabbed sections:
 * a Full pane with section banners, plus one tab per derived chapter
 * (UAT round 5: "the transcript needs to be broken down"). Unsynced
 * transcripts (no timestamps) get a single Full section — there's no
 * timeline to split on.
 */
export function deriveTranscriptSections(
  parsed: ParsedTranscript,
): DerivedTranscriptSection[] {
  if (parsed.cues.length === 0) return [];
  if (!parsed.synced) {
    return [
      {
        key: "full",
        label: "Full Transcript",
        blocks: parsed.cues.map(cueToLine),
      },
    ];
  }

  const cues = [...parsed.cues].sort((a, b) => a.startMs - b.startMs);
  const durationMs = Math.max(totalDurationMs(cues), 60_000);
  const chapters = deriveChapters(cues, durationMs);

  const sections: DerivedTranscriptSection[] = chapters.map((ch, n) => {
    const startIdx = cues.findIndex((c) => c.startMs >= ch.startMs);
    const endIdx =
      n + 1 < chapters.length
        ? cues.findIndex((c) => c.startMs >= chapters[n + 1]!.startMs)
        : cues.length;
    const slice = cues.slice(startIdx, endIdx === -1 ? cues.length : endIdx);
    const banner: TranscriptBlock = {
      kind: "banner",
      id: `dtb-${n}`,
      label: ch.title,
      time: `${ch.timestamp} · ${ch.duration}`,
    };
    return {
      key: ch.id,
      label: snippet(ch.title.replace(/^\d+\. /, ""), 4),
      blocks: [banner, ...slice.map((c, i) => cueToLine(c, startIdx + i))],
    };
  });

  const full: DerivedTranscriptSection = {
    key: "full",
    label: "Full Transcript",
    blocks: sections.flatMap((s) => s.blocks),
  };

  // A single chapter adds no navigation — the Full pane suffices.
  return sections.length > 1 ? [full, ...sections] : [full];
}
